import { createContext, useContext, useState, useEffect } from 'react';
import { registerCartListener } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

const cartKey = (userId) => userId ? `cart_${userId}` : 'cart_guest';
const loadCart = (userId) => {
  try { return JSON.parse(localStorage.getItem(cartKey(userId))) || []; } catch { return []; }
};

export const CartProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [items, setItems] = useState(() => loadCart(null));

  useEffect(() => {
    // Register once so AuthContext can call us on login/logout
    registerCartListener((newUserId) => {
      setUserId(prev => {
        if (newUserId === prev) return prev;

        if (newUserId) {
          // Login: merge guest cart into user cart
          const guestItems = loadCart(null);
          const userItems = loadCart(newUserId);
          const merged = [...userItems];
          guestItems.forEach(gi => {
            const existing = merged.find(i => i._id === gi._id);
            if (existing) {
              existing.quantity = Math.min(existing.quantity + gi.quantity, gi.stock || 999);
            } else {
              merged.push(gi);
            }
          });
          localStorage.removeItem(cartKey(null));
          localStorage.setItem(cartKey(newUserId), JSON.stringify(merged));
          setItems(merged);
        } else {
          // Logout: clear items, load guest cart (empty by default)
          if (prev) localStorage.setItem(cartKey(prev), JSON.stringify([]));
          setItems(loadCart(null));
        }
        return newUserId;
      });
    });
  }, []);

  // Persist on every change
  useEffect(() => {
    localStorage.setItem(cartKey(userId), JSON.stringify(items));
  }, [items, userId]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i._id === product._id);
      if (existing) {
        const newQty = Math.min(existing.quantity + quantity, product.stock);
        toast.success('Cart updated');
        return prev.map(i => i._id === product._id ? { ...i, quantity: newQty } : i);
      }
      toast.success(`${product.name} added to cart`);
      return [...prev, { ...product, quantity: Math.min(quantity, product.stock) }];
    });
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i._id !== id));

  const updateQty = (id, qty) => {
    if (qty < 1) return removeItem(id);
    setItems(prev => prev.map(i => i._id === id ? { ...i, quantity: qty } : i));
  };

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);